<template>
  <el-dialog
    title="导入费用"
    :visible.sync="visible"
    width="500px"
    :before-close="handleClose"
    :close-on-click-modal="false"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="120px"
      class="text-left"
    >
      <!-- 文件选择区域 -->
      <el-form-item label="选择导入文件" prop="file">
        <el-upload
          ref="upload"
          action=""
          :auto-upload="false"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          accept=".xls,.xlsx"
          :limit="1"
          :file-list="fileList"
        >
          <el-button size="small" type="primary">点击上传</el-button>
          <div slot="tip" class="el-upload__tip">
            {{ fileName || "请选择.xls/.xlsx格式的文件，大小不超过2MB" }}
          </div>
        </el-upload>
      </el-form-item>

      <!-- 模板下载区域 -->
      <el-form-item label="下载导入模板">
        <span class="margin-left">请先下载模板，按照格式填写数据后再导入</span>
        <el-link
          type="primary"
          href="/import/importFundPayment.xlsx"
          target="_blank"
        >
          费用导入模板
        </el-link>
      </el-form-item>

      <!-- 导入提示 -->
      <el-form-item label="导入提示">
        <div class="import-tips">
          <p>1. 仅支持 .xls/.xlsx 格式文件，单次导入不超过 1000 条</p>
          <p>2. 费用类别需与系统中配置的一致，否则导入失败</p>
          <p>3. 费用金额需为数字格式，保留2位小数</p>
          <p>4. 导入结果将在日志详情页查看</p>
        </div>
      </el-form-item>
    </el-form>

    <!-- 底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleImport" :loading="uploading">
        导入
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { importFundPayments } from "@/api/fee/fundPaymentApi";

export default {
  name: "ImportFee",
  data() {
    return {
      visible: false,
      uploading: false,
      fileList: [], // 文件列表，用于upload组件管理
      form: {
        communityId: this.getCommunityId ? this.getCommunityId() : "",
        file: null,
      },
      fileName: "",
      rules: {
        file: [
          { required: true, message: "请选择要导入的文件", trigger: "change" },
        ],
      },
    };
  },
  methods: {
    // 打开弹窗
    open() {
      this.visible = true;
      this.resetForm();
    },

    // 关闭弹窗
    handleClose() {
      this.visible = false;
      this.resetForm();
    },

    // 重置表单和上传状态
    resetForm() {
      this.form.file = null;
      this.fileName = "";
      this.uploading = false;
      this.fileList = [];

      if (this.$refs.upload) {
        this.$refs.upload.clearFiles();
      }

      // 清除表单验证
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
    },

    // 文件选择事件
    handleFileChange(file, fileList) {
      // 更新文件列表
      this.fileList = fileList.slice(-1); // 只保留最后一个文件

      // 更新表单数据
      this.form.file = file.raw;
      this.fileName = file.name;

      // 清除验证错误
      if (this.$refs.form) {
        this.$refs.form.validateField("file");
      }
    },

    // 文件移除事件
    handleFileRemove() {
      this.form.file = null;
      this.fileName = "";
      this.fileList = [];

      // 清除验证错误
      if (this.$refs.form) {
        this.$refs.form.validateField("file");
      }
    },

    // 导入前校验
    validate() {
      // 1. 校验文件是否选择
      if (!this.form.file) {
        this.$message.error("请选择要导入的文件");
        return false;
      }

      // 2. 校验文件格式
      const fileExt = this.fileName
        .substring(this.fileName.lastIndexOf(".") + 1)
        .toLowerCase();
      if (!["xls", "xlsx"].includes(fileExt)) {
        this.$message.error("仅支持.xls/.xlsx格式的Excel文件");
        return false;
      }

      // 3. 校验文件大小（2MB）
      const maxSize = 2 * 1024 * 1024;
      if (this.form.file.size > maxSize) {
        this.$message.error(`上传文件大小不能超过${maxSize / 1024 / 1024}MB`);
        return false;
      }

      // 4. 校验园区ID
      if (!this.form.communityId) {
        this.$message.error("获取园区ID失败，请刷新页面重试");
        return false;
      }

      return true;
    },

    // 处理导入
    async handleImport() {
      if (!this.validate()) return;

      try {
        this.uploading = true;

        // 构建FormData
        const formData = new FormData();
        formData.append("uploadFile", this.form.file);
        formData.append("communityId", this.form.communityId);
        formData.append("importAdapt", "importFundPayment");

        // 调用导入接口
        const response = await importFundPayments(formData);

        // 导入成功提示
        this.$message.success("费用导入提交成功，正在后台处理中...");

        // 跳转日志详情页
        this.$router.push({
          path: "/views/system/assetImportLogDetail",
          query: {
            logId: response.data.logId,
            logType: "importFundPayment",
          },
        });

        // 关闭弹窗并通知父组件刷新
        this.handleClose();
        this.$emit("handleRefreshFee");
      } catch (error) {
        console.error("导入失败:", error);
        let errorMessage =
          error.message || "导入失败，请检查文件内容或联系管理员";

        // 如果错误包含详细信息，显示更具体的错误
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || errorMessage;
        }

        this.$message.error(errorMessage);
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.margin-left {
  margin-left: 10px;
}

.text-left {
  text-align: left;
}

.import-tips {
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
  margin-left: 10px;
}

.dialog-footer {
  text-align: center;
  display: block;
}

.el-upload__tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

/* 表单验证错误样式 */
::v-deep .el-form-item.is-error .el-input__inner {
  border-color: #f56c6c;
}
</style>
